import request from 'superagent';
import DeferredUtility from '../utility/DeferredUtility';

export type FieldValidationErrorsType = { [ruleName: string]: string };
export type NestedValidationErrorsType = { [fieldName: string]: { [ruleName: string]: string } | NestedValidationErrorsType | NestedValidationErrorsType[] };

type SuperagentProgressInfoType = {
  lengthComputable: boolean,
  percent: number;  // 転送された割合（0〜100の範囲の数値）
  total: number;    // 総データサイズ（バイト数）
  loaded: number;   // 転送済みのデータ量（バイト数）  
};

type AjaxGetFieldsType = {
  [key: string]: string,
}
type AjaxPostFieldsType = {
  [key: string]: string|FileList|File|null
}
type AjaxPostOptionsType = {
  progress?: (params: SuperagentProgressParams) => void,
}
export type AjaxResponseType = {
  result: boolean,
  msg: string,
  validationErrors?: NestedValidationErrorsType,
}
type StructuredFormData<T extends string|File|FileList> = { [key: string]: StructuredFormDataValue<T> }
type StructuredFormDataValue<T extends string|File|FileList> = T|StructuredFormData<T>|StructuredFormData<T>[];

// @see https://github.com/ladjs/superagent/blob/master/src/client.js
export type SuperagentProgressParams = {
  progress: number,
  percent: string,
  remaining: string,
}

/**
 * ローディングを表示する
 * @deprecated window.startLoading() / window.stopLoading() を直接呼ぶ
 * @param {HTMLFormElement} form フォーム
 * @param {boolean} visible 表示するかどうか
 */
export function setLoadingVisible(form: HTMLFormElement, visible: boolean) {
  if (visible) {
    window.startLoading();
  } else {
    window.stopLoading();
  }
}

/**
 * エラーをポップアップ表示する。
 * @deprecated
 * @param {object} errors { [name]: [[ruleName, message]] } 型式のエラー情報
 */
export function displayErrors(validationErrors: { [key: string]: FieldValidationErrorsType }) {
  Object.values(validationErrors).forEach((validationError) => {
    Object.values(validationError).forEach((message) => {
      showErrorFlush(message);
    });
  });
}

/**
 * ajaxでGETする。
 * @param {string} url 送信先URL
 * @param {object} query 送信パラメーター
 * @returns promiseを返す。通信が終わった後の処理はthenで書く。
 */
export function ajaxGet<T>(url: string, query: AjaxGetFieldsType = {}, suppressFlush: boolean = false): Promise<T&AjaxResponseType> {
  const r = request.get(url)
    .set('X-Requested-With', 'XMLHttpRequest')
    .query(query);
  return handleSuperagentRequest(r, suppressFlush);
}

/**
 * ajaxでPOSTする。
 * 対応するのはファイルアップロードを含むフォームデータ。
 * JSON POSTはしない。
 * @param {string} url 送信先URL
 * @param {object} values 送信する値
 * @param {object} options 進捗をみたい時にはprogressを使う。
 * @returns promiseを返す。通信が終わった後の処理はthenで書く。
 */
export function ajaxPost<T>(url: string, values: AjaxPostFieldsType = {}, options: AjaxPostOptionsType = {}, suppressFlush: boolean = false): Promise<T&AjaxResponseType> {
  const r = request.post(url)
    .set('X-Requested-With', 'XMLHttpRequest');
  if (options.progress) {
    const startTime = new Date().getTime();
    const progressCallback = options.progress
    r.on('progress', (e: SuperagentProgressInfoType) => {
      progressCallback(buildSuperagentProgressParams(startTime, e));
    });
  }
  Object.entries(values).forEach(([name, value]) => {
    if (typeof value === 'undefined' || value === null) return;
    if (value instanceof window.FileList) {
      for (let i = 0; i < value.length; i += 1) {
        // @ts-ignore
        r.attach(name, value[i]);
      }
    } else if (value instanceof window.File) {
      // @ts-ignore
      r.attach(name, value);
    } else {
      r.field(name, value);
    }
  });
  return handleSuperagentRequest(r, suppressFlush);
}

export function showSuccessFlush(message: string): void {
  const html = `<div class="toast-message"><span class="material-symbols-outlined">check_circle</span>${message}</div>`;
  window.jQuery.jGrowl(html, { theme: 'jGrowl-notification-success', closeTemplate: '' });
}

export function showErrorFlush(message: string): void {
  const html = `<div class="toast-message"><span class="material-symbols-outlined">cancel</span>${message}</div>`;
  window.jQuery.jGrowl(html, { theme: 'jGrowl-notification-error', closeTemplate: '' });
}

function buildSuperagentProgressParams(startTime: number, e: SuperagentProgressInfoType): SuperagentProgressParams {
  let remaining: string|null = null;
  let percent: string|null = null;
  let progress = 0;
  if (e.lengthComputable) {
    progress = e.percent * 0.01;
    percent = `${Math.floor(e.percent)}%`;
    remaining = '計算中...';
    const currentTime = new Date().getTime();
    if (currentTime - startTime > 1000) {
      const bps = (e.loaded * 1000.0) / (currentTime - startTime);
      const seconds = (e.total - e.loaded) / bps;
      if (seconds < 60) {
        remaining = `${Math.floor(seconds)}秒`;
      } else if (seconds < 300) {
        const minutes = Math.floor(seconds / 60);
        remaining = `${minutes}分${Math.floor(seconds - 60 * minutes)}秒`;
      } else if (seconds < 3600) {
        remaining = `${Math.floor(seconds / 60)}分`;
      } else {
        remaining = '1時間以上';
      }
    }
  } else {
    percent = '不明';
    remaining = '不明';
  }
  return { progress, percent, remaining };
}

function handleSuperagentRequest<T extends AjaxResponseType>(r: request.Request, suppressFlush: boolean): Promise<T> {
  const d = DeferredUtility.newDeferred();
  const responseHandler = (res: request.Response|null, err: request.ResponseError|null) => {
    let extraResponseData: { [key: string]: any } = {};
    let responseData: AjaxResponseType
    if (res) {
      if (res.body) {
        responseData = { result: false, msg: '' };
        Object.entries(res.body).forEach(([name, value]) => {
          if (name === 'validationErrors') {
            responseData.validationErrors = value as NestedValidationErrorsType;
          } else {
            extraResponseData[name] = value;
          }
        });
      } else {
        responseData = { result: false, msg: 'malformed response object' };
      }
    } else if (err) {
      if (!err.status) {
        responseData = { result: false, msg: '送信が失敗しました。通信状態の良いところで再度お試しください。' };
      } else if (err.message) {
        responseData = { result: false, msg: err.message };
      } else {
        responseData = { result: false, msg: '不明なエラーです。' };
      }
    } else {
      responseData = { result: false, msg: '不明なエラーです。' };
    }
    if (responseData.errors) {
      if (!suppressFlush) {
        displayErrors(responseData.validationErrors as { [key: string]: FieldValidationErrorsType });
      }
      window.Frekul.CommonUI.PopupManager.resizeAllPopups();
    } else if (!responseData.result && responseData.msg) {
      if (!suppressFlush) {
        showErrorFlush(responseData.msg);
      }
    }
    d.resolve({ ...responseData, ...extraResponseData } as T);
  };
  r.then((res: request.Response) => {
    responseHandler(res, null);
  }).catch((err: request.ResponseError) => {
    responseHandler(null, err);
  });
  return DeferredUtility.convertJQueryPromise(d.promise());
}

